import { ButtonVariant, CoButton } from "components/CoButton";
import Dropdown, { CoDropdownItemProps } from "components/CoDropdown";
import Tooltip from "components/Tooltip";
import { trackEventServerSide } from "helpers/api";
import {
  MixpanelActionSource,
  MixpanelEvent,
} from "helpers/services/mixpanel/events";
import { getMajorVersion } from "helpers/services/mixpanel";
import useLabels from "hooks/useLabels";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useCallback, useEffect, useLayoutEffect } from "react";
import { Rows2, Rows3, Table } from "lucide-react";
import { ToggleGroup } from "design_system";
import { useTableViewNewTag } from "features/Thread/hooks/useTableViewNewTag";
import Tag from "components/Tag";

export enum ResultFormat {
  COMPACT = "COMPACT",
  DEFAULT = "DEFAULT",
  TABLE = "TABLE",
}

const THREAD_RESULT_FORMAT_PREFIX = "thread_result_format_";
const GLOBAL_RESULT_FORMAT_KEY = "global_result_format";

type ResultFormatButtonProps = {
  currentFormat: ResultFormat;
  onFormatChange: (format: ResultFormat) => void;
  disabled?: boolean;
  label?: string;
  actionSource?: MixpanelActionSource;
  threadInteractionId?: string;
  useGlobalPreference?: boolean;
  isTableViewEnabled?: boolean;
};

/**
 * @component ResultFormatButton
 * @description Toggle between compact and detailed result formats
 */
const ResultFormatButton = ({
  currentFormat,
  onFormatChange,
  disabled,
  actionSource,
  threadInteractionId,
  isTableViewEnabled = false,
}: ResultFormatButtonProps): JSX.Element => {
  const [resultFormatLabels] = useLabels(
    "features.thread-interaction-results.results-view-mode"
  );

  const { isTableViewNewTagHidden, dismissTableViewNewTag } =
    useTableViewNewTag();

  // Storage key for thread-specific result format
  const threadStorageKey = threadInteractionId
    ? `${THREAD_RESULT_FORMAT_PREFIX}${threadInteractionId}`
    : null;

  // Use local storage for thread-specific result format
  const [threadFormat, setThreadFormat] =
    useLocalStorageState<ResultFormat | null>(
      threadStorageKey || "dummy_key",
      null
    );

  // Use local storage for global preference
  const [globalFormat, setGlobalFormat] =
    useLocalStorageState<ResultFormat | null>(GLOBAL_RESULT_FORMAT_KEY, null);

  // If there's no global format, set global format to DEFAULT
  useEffect(() => {
    if (!globalFormat) {
      setGlobalFormat(ResultFormat.DEFAULT);
    }
  }, [globalFormat, setGlobalFormat]);

  // If first time rendering thread interaction and no thread format set for thread interaction, set thread format to global format
  useEffect(() => {
    if (threadInteractionId && !threadFormat && globalFormat) {
      setThreadFormat(globalFormat);
    }
  }, [threadInteractionId, threadFormat, globalFormat, setThreadFormat]);

  useLayoutEffect(() => {
    // Wait for globalFormat to be initialized before applying any format changes
    if (!globalFormat) {
      return;
    }

    if (threadInteractionId && threadFormat && threadFormat !== currentFormat) {
      // 1. Use thread-specific format if available
      handleFormatChange(threadFormat, false);
      return;
    }

    if (globalFormat && globalFormat !== currentFormat && !threadFormat) {
      // 2. Fall back to global default format if available and no thread format exists
      handleFormatChange(globalFormat, false);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    globalFormat,
    threadFormat,
    currentFormat,
    onFormatChange,
    threadInteractionId,
  ]);

  const handleFormatChange = useCallback(
    (format: ResultFormat, setGlobalState: boolean) => {
      if (setGlobalState) {
        setGlobalFormat(format);
      }

      if (threadInteractionId) {
        setThreadFormat(format);
      }

      onFormatChange(format);

      if (
        isTableViewEnabled &&
        format === ResultFormat.TABLE &&
        !isTableViewNewTagHidden
      ) {
        dismissTableViewNewTag();
      }

      // Track the result view mode change event
      trackEventServerSide({
        event: MixpanelEvent.ResultViewModeChanged,
        version: getMajorVersion(),
        resultViewMode: format,
      });
    },
    [
      onFormatChange,
      threadInteractionId,
      setThreadFormat,
      setGlobalFormat,
      isTableViewEnabled,
      isTableViewNewTagHidden,
      dismissTableViewNewTag,
    ]
  );

  const dropdownItems: CoDropdownItemProps[] = [
    {
      key: ResultFormat.DEFAULT,
      onClick: () => handleFormatChange(ResultFormat.DEFAULT, true),
      leftIcon: <i className="icon-results-detailed text-xl" />,
      title: resultFormatLabels["default"],
    },
    {
      key: ResultFormat.COMPACT,
      onClick: () => handleFormatChange(ResultFormat.COMPACT, true),
      leftIcon: <i className="icon-results-compact text-xl" />,
      title: resultFormatLabels["compact"],
    },
  ];

  const viewOptions = [
    {
      id: ResultFormat.DEFAULT,
      "aria-label": resultFormatLabels["default"],
      onClick: () => handleFormatChange(ResultFormat.DEFAULT, true),
      icon: <Rows2 size={20} />,
    },
    {
      id: ResultFormat.COMPACT,
      "aria-label": resultFormatLabels["compact"],
      onClick: () => handleFormatChange(ResultFormat.COMPACT, true),
      icon: <Rows3 size={20} />,
    },
    {
      id: ResultFormat.TABLE,
      "aria-label": resultFormatLabels["table"],
      onClick: () => handleFormatChange(ResultFormat.TABLE, true),
      icon: !isTableViewNewTagHidden ? (
        <div className="flex items-center gap-2">
          <Table size={20} />
          <Tag
            label="New"
            size="sm"
            className="bg-accent-bg-base! text-accent-fg-on-accent!"
          />
        </div>
      ) : (
        <Table size={20} />
      ),
    },
  ];

  return isTableViewEnabled ? (
    <ToggleGroup
      options={viewOptions}
      selectedOption={viewOptions.find((option) => option.id === currentFormat)}
      onChange={(option) => handleFormatChange(option.id as ResultFormat, true)}
    />
  ) : (
    <Dropdown
      items={dropdownItems}
      title={resultFormatLabels["result-format"]}
      useRadioIndicators
      selectedKey={currentFormat}
    >
      <Tooltip content={resultFormatLabels["result-format"]}>
        <CoButton
          variant={ButtonVariant.Flat}
          icon={
            currentFormat === ResultFormat.COMPACT
              ? "icon-results-compact"
              : "icon-results-detailed"
          }
          iconRight="icon-chevron-down"
          size="sm"
          disabled={disabled}
        />
      </Tooltip>
    </Dropdown>
  );
};

export default ResultFormatButton;
export { GLOBAL_RESULT_FORMAT_KEY };
